import { useState, useEffect } from 'react';
import { getDividends } from '../../utils/api';
import { fmt } from '../../utils/format';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';

export default function DividendTab({ symbol }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getDividends(symbol).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [symbol]);

  if (loading) return <div style={{ padding: 24 }}>{[1,2,3].map(i => <div key={i} className="skeleton card" style={{ height: 80, marginBottom: 12 }} />)}</div>;
  if (!data) return <div className="error-msg">Unable to load dividend data.</div>;

  const noDividend = !data.dividendYield && !data.dividendRate && data.history.length === 0;
  if (noDividend) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 48 }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>—</div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 15 }}>This stock does not pay a dividend.</div>
      </div>
    );
  }

  // Build annual summary from history
  const byYear = {};
  for (const d of data.history) {
    const yr = d.date.slice(0, 4);
    byYear[yr] = (byYear[yr] || 0) + d.amount;
  }
  const annualData = Object.entries(byYear).sort(([a], [b]) => a.localeCompare(b))
    .map(([year, total]) => ({ year, total: +total.toFixed(4) }));

  // Color bars: last bar accent, rest muted
  const barColor = (i) => i === annualData.length - 1 ? 'var(--accent)' : '#4b5563';

  return (
    <div>
      {/* Summary Stats */}
      <div className="card">
        <div className="card-title">Dividend Summary</div>
        <div className="grid-4">
          {data.dividendYield != null && (
            <div className="stat-cell">
              <div className="stat-label">Annual Yield</div>
              <div className="stat-value" style={{ color: 'var(--green)', fontSize: 20 }}>{fmt.pct(data.dividendYield * 100, 2)}</div>
            </div>
          )}
          {data.dividendRate != null && (
            <div className="stat-cell">
              <div className="stat-label">Annual Rate</div>
              <div className="stat-value">{fmt.currency(data.dividendRate)}</div>
            </div>
          )}
          {data.payoutRatio != null && (
            <div className="stat-cell">
              <div className="stat-label">Payout Ratio</div>
              <div className="stat-value" style={{ color: data.payoutRatio > 0.9 ? 'var(--red)' : data.payoutRatio > 0.6 ? 'var(--yellow)' : 'var(--text-primary)' }}>
                {fmt.pct(data.payoutRatio * 100, 1)}
              </div>
            </div>
          )}
          {data.fiveYearAvgDividendYield != null && (
            <div className="stat-cell">
              <div className="stat-label">5Y Avg Yield</div>
              <div className="stat-value">{fmt.pct(data.fiveYearAvgDividendYield, 2)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Upcoming Dates */}
      {(data.exDividendDate || data.nextDividendDate) && (
        <div className="card">
          <div className="card-title">Upcoming Dates</div>
          <div className="grid-2">
            {data.exDividendDate && (
              <div className="stat-cell">
                <div className="stat-label">Ex-Dividend Date</div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 16, fontWeight: 600, color: 'var(--yellow)', marginTop: 4 }}>
                  {new Date(data.exDividendDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </div>
            )}
            {data.nextDividendDate && (
              <div className="stat-cell">
                <div className="stat-label">Next Payment Date</div>
                <div style={{ fontFamily: 'JetBrains Mono', fontSize: 16, fontWeight: 600, color: 'var(--green)', marginTop: 4 }}>
                  {new Date(data.nextDividendDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Annual Dividend Chart */}
      {annualData.length > 0 && (
        <div className="card">
          <div className="card-title">Annual Dividend per Share ($)</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={annualData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              <XAxis dataKey="year" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickFormatter={v => `$${v.toFixed(2)}`} />
              <Tooltip
                contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12 }}
                formatter={v => [`$${v.toFixed(4)}`, 'Dividend']}
              />
              <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                {annualData.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* History Table */}
      {data.history.length > 0 && (
        <div className="card">
          <div className="card-title">Dividend History</div>
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Ex-Date</th>
                  <th style={{ textAlign: 'left' }}>
                    Pay Date
                    {data.payOffsetDays != null && (
                      <span style={{ fontSize: 9, color: 'var(--text-muted)', marginLeft: 4, fontWeight: 400 }}>
                        (est. +{data.payOffsetDays}d)
                      </span>
                    )}
                  </th>
                  <th>Amount</th>
                  <th>vs Prev</th>
                </tr>
              </thead>
              <tbody>
                {data.history.map((d, i) => {
                  const prev = data.history[i + 1];
                  const chg = prev ? ((d.amount - prev.amount) / prev.amount) * 100 : null;
                  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
                  return (
                    <tr key={d.date}>
                      <td style={{ textAlign: 'left', fontFamily: 'inherit' }}>{fmtDate(d.date)}</td>
                      <td style={{ textAlign: 'left', fontFamily: 'inherit', color: d.payDate ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                        {fmtDate(d.payDate)}
                      </td>
                      <td>{fmt.currency(d.amount)}</td>
                      <td style={{ color: chg == null ? 'var(--text-muted)' : chg >= 0 ? 'var(--green)' : 'var(--red)' }}>
                        {chg == null ? '—' : `${chg >= 0 ? '+' : ''}${chg.toFixed(1)}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
